<template>
  <q-select
    ref="select"
    dense
    outlined
    :disable="disableComputed"
    :readonly="readonlyComputed"
    :options="outputOptions"
    :rules="computedRules"
    emit-value
    map-options
    :clearable="!emptyOptionLabel && !noClear"
    hide-bottom-space
    :loading="filtering || $attrs.loading"
    :use-input="useInput"
    :hide-selected="useInput"
    :fill-input="useInput"
    :dark="dark"
    :color="color"
    popup-content-class="app-select-popup"
    @filter="filterFunction"
  >
    <template v-for="(slot, name) in $slots" #[name]="scope">
      <slot :name="name" v-bind="scope ?? {}" />
    </template>
    <template v-if="infoButtonId || infoButtonKey" #after>
      <slot name="after" />
      <app-info-button :identifier="infoButtonId" :translation-key="infoButtonKey" />
    </template>
  </q-select>
</template>

<script setup>
import { ref, watch, computed } from "vue"
import { storeToRefs } from "pinia"

import { useDictionariesStore } from "@/stores/dictionaries.js"
import { useTranslationsStore } from "@/stores/translations.js"
import { useModuleDataStore } from "@/stores/moduleData.js"

import * as comparisonService from "@/services/comparisonService.js"
import * as quasarUtils from "@/services/quasarUtils.js"

import { useValidators } from "@/composables/useValidators.js"
import { useFieldsetState } from "@/composables/useFieldsetState.js"

const validators = useValidators()
const dictionariesStore = useDictionariesStore()
const moduleStore = useModuleDataStore()

const props = defineProps({
  options: {
    type: Array,
    default: () => []
  },
  dictionaryName: {
    type: String,
    default: null
  },
  dictionaryKeysAsNumbers: {
    type: Boolean,
    default: false
  },
  emptyOptionLabel: {
    type: String,
    default: null
  },
  useInput: Boolean,
  useInputMinChars: Number,
  noClear: Boolean,
  dark: Boolean,
  filterHandler: Function,
  itemFormatter: Function,
  required: Boolean,
  disable: Boolean,
  readonly: Boolean,
  formStateInheritanceDisabled: Boolean,
  rules: Array,
  showValueOnly: Boolean,
  showKeyOnly: Boolean,
  sortByKey: Boolean,
  sortByValue: Boolean,
  infoButtonId: String,
  infoButtonKey: String,
  requiredCondition: Boolean,
  requiredConditionKey: String,
  whiteList: Array,
  blackList: Array
})

const select = ref(null)
const filtering = ref(false)

defineExpose({ validate: () => select.value.validate() })

const outputOptions = ref([])
const allOptions = ref([])

const { disableComputed, readonlyComputed } = useFieldsetState(props)

const { changingLanguage } = storeToRefs(useTranslationsStore())

const color = computed(() => {
  if (props.dark) {
    return "grey-4"
  }
  return null
})

const isRequired = computed(() => {
  if (props.required) {
    return true
  }

  if (props.requiredCondition || props.requiredConditionKey) {
    const conditionKey = props.requiredConditionKey || props.infoButtonKey
    return moduleStore.moduleSettings.requiredFields?.includes(conditionKey)
  }

  return false
})

const computedRules = computed(() => {
  const rules = props.rules ? [...props.rules] : []
  if (isRequired.value) {
    rules.push(validators.required)
  }
  return rules
})

const getLabelForDictionaryItem = (item) => {
  if (props.showValueOnly) {
    return dictionariesStore.getValueForKey(props.dictionaryName, item.Key)
  } else if (props.showKeyOnly) {
    return item.Key
  } else {
    return dictionariesStore.getFormattedDictionaryValueFromDictionary(props.dictionaryName, item.Key)
  }
}

const setOutputOptions = () => {
  let items = []
  if (props.dictionaryName) {
    const dict = dictionariesStore.getDictionary(props.dictionaryName)

    if (props.sortByKey) {
      dict.sort((a, b) => comparisonService.compare(a, b, "Key"))
    }

    if (props.sortByValue) {
      dict.sort((a, b) => comparisonService.compare(a, b, "Value"))
    }

    items = dict.map((item) => ({
      label: getLabelForDictionaryItem(item),
      value: props.dictionaryKeysAsNumbers ? parseInt(item.Key, 10) : item.Key
    }))
  } else if (props.options.length && typeof props.options[0] !== "object") {
    items = props.options.map((option) => ({
      label: option,
      value: option
    }))
  } else {
    items = props.options.map((option) => ({
      label: option.label,
      value: option.value
    }))
  }

  if (props.emptyOptionLabel) {
    items.unshift({
      label: props.emptyOptionLabel,
      value: null
    })
  }

  if (props.whiteList) {
    items = items.filter((item) => props.whiteList.includes(item.value))
  } else if (props.blackList) {
    items = items.filter((item) => !props.blackList.includes(item.value))
  }

  outputOptions.value = items
  allOptions.value = items.slice()
}

const filterFunction = async (val, update) => {
  if (!props.useInput || (props.useInputMinChars && val.length < props.useInputMinChars)) {
    update()
    return
  }

  if (props.filterHandler) {
    try {
      filtering.value = true
      const { items } = await props.filterHandler(val)
      filtering.value = false
      update(() => {
        outputOptions.value = items.map((item) => (props.itemFormatter ? props.itemFormatter(item) : item))
      })
    } catch (err) {
      quasarUtils.handleError(err)
    }
  } else {
    update(() => {
      outputOptions.value = allOptions.value.filter((v) => !val || v.label.toLowerCase().indexOf(val.toLowerCase()) > -1)
    })
  }
}

watch(() => props.options, setOutputOptions, { immediate: true })

watch(
  () => props.whiteList,
  () => {
    setOutputOptions()
  }
)

watch(
  () => changingLanguage.value,
  (newValue) => {
    if (!newValue) {
      setOutputOptions()
    }
  }
)
</script>

<style lang="scss">
.app-select-popup {
  .q-item--dark.q-item--active {
    color: $accent;
  }
}
</style>
